import { useMemo } from "react";
import { FilterSelect } from "@/components/atoms/filters/FilterSelect";
import { DateRangeFilter } from "@/components/atoms/filters/DateRangeFilter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { FilterX } from "lucide-react";
import type { District } from "@/models/district";
import type { Region } from "@/models/region";

interface RequestFiltersProps {
  statusFilter: string;
  onStatusChange: (value: string) => void;
  districtFilter: string;
  onDistrictChange: (value: string) => void;
  regionFilter: string;
  onRegionChange: (value: string) => void;
  dateFromFilter: string;
  onDateFromChange: (value: string) => void;
  dateToFilter: string;
  onDateToChange: (value: string) => void;
  districts: District[];
  regions: Region[];
  isScoped?: boolean; // When true, hide district/region filters
  showRegionOnly?: boolean; // When true in district scope, show only region filter
}

const statusOptions = [
  { value: "DRAFT", label: "Borrador" },
  { value: "SUBMITTED", label: "Enviado" },
  { value: "APPROVED", label: "Aprobado" },
  { value: "PRINTED", label: "Impreso" },
  { value: "DELIVERED", label: "Entregado" },
  { value: "REJECTED", label: "Observado" },
];

/**
 * Organism: Request filters for the credential request list
 * Contains status, district, region, and date range filters
 */
export function RequestFilters({
  statusFilter,
  onStatusChange,
  districtFilter,
  onDistrictChange,
  regionFilter,
  onRegionChange,
  dateFromFilter,
  onDateFromChange,
  dateToFilter,
  onDateToChange,
  districts,
  regions,
  isScoped = false,
  showRegionOnly = false,
}: RequestFiltersProps) {
  // Memoize options to prevent unnecessary recalculations
  const districtOptions = useMemo(
    () => districts.map((d) => ({
      value: d.id,
      label: d.name || "Sin nombre",
    })),
    [districts]
  );

  const regionOptions = useMemo(
    () => regions.map((r) => ({
      value: r.id,
      label: r.name || "Sin nombre",
    })),
    [regions]
  );

  // Count active filters
  const activeFilterCount = useMemo(
    () => [
      statusFilter,
      districtFilter,
      regionFilter,
      dateFromFilter,
      dateToFilter,
    ].filter(Boolean).length,
    [statusFilter, districtFilter, regionFilter, dateFromFilter, dateToFilter]
  );

  // Clear all filters
  const handleClearAll = () => {
    onStatusChange("");
    onDistrictChange("");
    onRegionChange("");
    onDateFromChange("");
    onDateToChange("");
  };

  return (
    <div className="space-y-3">
      {/* Header with active filter count and clear all button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Filtros
          </span>
          {activeFilterCount > 0 && (
            <Badge variant="secondary" className="text-xs">
              {activeFilterCount} activo{activeFilterCount > 1 ? "s" : ""}
            </Badge>
          )}
        </div>
        {activeFilterCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleClearAll}
            className="h-8 gap-2 text-xs"
          >
            <FilterX className="size-4" />
            Limpiar todo
          </Button>
        )}
      </div>

      {/* Filter grid */}
      <div className="flex flex-wrap gap-4 justify-center">
        <FilterSelect
          label="Estado"
          value={statusFilter}
          onChange={onStatusChange}
          options={statusOptions}
          placeholder="Todos"
        />
        {!isScoped && (
          <FilterSelect
            label="Distrito"
            value={districtFilter}
            onChange={onDistrictChange}
            options={districtOptions}
            placeholder="Todos"
          />
        )}
        {(!isScoped || showRegionOnly) && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <FilterSelect
                    label="Región"
                    value={regionFilter}
                    onChange={onRegionChange}
                    options={regionOptions}
                    placeholder="Todos"
                    disabled={!isScoped && !districtFilter}
                  />
                </div>
              </TooltipTrigger>
              {!isScoped && !districtFilter && (
                <TooltipContent>
                  <p>Seleccione un distrito primero</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        )}
        <DateRangeFilter
          dateFrom={dateFromFilter}
          dateTo={dateToFilter}
          onDateFromChange={onDateFromChange}
          onDateToChange={onDateToChange}
          label="Fecha de creación"
        />
      </div>
    </div>
  );
}
